import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import logo from '../assets/logo.png';
import $ from 'jquery';
import 'datatables.net';
import 'datatables.net-dt';
import * as XLSX from 'xlsx';
import './TCOViewer.css';

const TCOViewer = () => {
    const tableRef = useRef(null);
    const dataTableRef = useRef(null);
    const tcoDataRef = useRef([]);
    const isMounted = useRef(false);
    const [tariffFilter, setTariffFilter] = useState('');
    const [tcoNumberFilter, setTcoNumberFilter] = useState('');
    const [descriptionFilter, setDescriptionFilter] = useState('');
    const isDrawing = useRef(false);
    const [isTableInitialized, setIsTableInitialized] = useState(false);
    const [isProDropdownOpen, setIsProDropdownOpen] = useState(false); // State for dropdown
    const navigate = useNavigate();

    const expectedHeaders = useMemo(() => ['Tariff Classification', 'TCO Number', 'TCO description'], []);

    const applyStyles = () => {
        const isDarkMode = $('body').hasClass('dark-mode');
        $('.custom-filter-input').css({
            height: '35px',
            borderRadius: '6px',
            padding: '8px 12px',
            width: '200px',
            fontSize: '14px',
            border: '1px solid ' + (isDarkMode ? '#777' : '#ced4da'),
            background: isDarkMode ? '#555' : '#fff',
            color: isDarkMode ? '#fff' : '#495057',
            boxSizing: 'border-box',
            lineHeight: '1.5',
            marginRight: '10px'
        });
        $('.dt-paging button.dt-paging-button').css({
            padding: '8px 12px',
            fontSize: '14px',
            fontWeight: '500',
            border: '1px solid ' + (isDarkMode ? '#777' : '#ced4da'),
            borderRadius: '6px',
            backgroundColor: isDarkMode ? '#555' : '#fff',
            color: isDarkMode ? '#ccc' : '#495057',
            cursor: 'pointer',
            margin: '0 4px',
            boxSizing: 'border-box',
            transition: 'background-color 0.2s, color 0.2s'
        });
        $('.dt-paging button.dt-paging-button:not(.disabled):hover').css({
            backgroundColor: isDarkMode ? '#666' : '#f0f4f8',
            color: isDarkMode ? '#fff' : '#007bff'
        });
        $('.dt-paging button.dt-paging-button.current').css({
            backgroundColor: isDarkMode ? '#007bff' : '#007bff',
            color: '#fff',
            borderColor: isDarkMode ? '#007bff' : '#007bff'
        });
        $('.dt-paging button.dt-paging-button.disabled').css({
            cursor: 'not-allowed',
            opacity: '0.5'
        });
        $('#tcoTable').addClass('table-visible');
    };

    useEffect(() => {
        if (isMounted.current) return;
        isMounted.current = true;

        const loadTcoData = async () => {
            if (tcoDataRef.current.length > 0) return;

            try {
                const response = await fetch('/TCOData.xlsx');
                if (!response.ok) throw new Error('Failed to fetch TCO data');
                const arrayBuffer = await response.arrayBuffer();
                const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                if (!firstSheet) throw new Error('Invalid Excel file');

                let loadedTcoData = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
                loadedTcoData = loadedTcoData.map(row => {
                    const newRow = {};
                    expectedHeaders.forEach(header => {
                        let value = row[header] !== undefined ? row[header] : '';
                        if (header === 'TCO Number' && value !== '') {
                            let num = String(value).replace(/^TC/i, '');
                            value = num.padStart(7, '0');
                        }
                        newRow[header] = value;
                    });
                    return newRow;
                });

                tcoDataRef.current = loadedTcoData;
            } catch (error) {
                alert('Failed to load TCO data. Please try again later.');
                tcoDataRef.current = [];
            }

            if (!tableRef.current) return;

            if (dataTableRef.current) {
                dataTableRef.current.destroy(false);
            }

            applyStyles();

            if (!$.fn.DataTable.isDataTable(tableRef.current)) {
                try {
                    dataTableRef.current = $(tableRef.current).DataTable({
                        columns: expectedHeaders.map(header => ({
                            title: header,
                            data: header,
                            render: (data, type, row, meta) => {
                                if (header === 'TCO Number' && data) {
                                    return String(data);
                                }
                                return data || '';
                            }
                        })),
                        columnDefs: [
                            { width: '120px', targets: 0 },
                            { width: '100px', targets: 1 },
                            { width: 'auto', targets: 2 }
                        ],
                        pageLength: 15,
                        lengthMenu: [15, 25, 50, 100],
                        searching: true,
                        ordering: true,
                        order: [[0, 'asc']],
                        responsive: true,
                        language: {
                            search: '',
                            searchPlaceholder: 'Filter records...',
                            lengthMenu: 'Show _MENU_ entries',
                        },
                        dom: '<"table-header"l>t<"table-footer"ip>'
                    });
                    dataTableRef.current.clear().rows.add(tcoDataRef.current).draw();
                    dataTableRef.current.on('draw', () => {
                        applyStyles();
                        isDrawing.current = false;
                    });
                    setIsTableInitialized(true);
                } catch (error) {
                    alert('Failed to initialize table.');
                }
            }
        };

        loadTcoData();

        return () => {
            if (dataTableRef.current && !document.getElementById('tcoTable')) {
                dataTableRef.current.destroy(false);
            }
            isMounted.current = false;
        };
    }, [expectedHeaders]);

    useEffect(() => {
        if (!dataTableRef.current || !isTableInitialized) return;

        const timeoutId = setTimeout(() => {
            if (isDrawing.current) return;
            isDrawing.current = true;

            $.fn.dataTable.ext.search = [];

            const tcoNumberFilterFunction = (settings, data, dataIndex, rowData, counter) => {
                if (settings.nTable.id !== 'tcoTable') return true;
                const tcoNumber = String(data[1] || '');
                return tcoNumberFilter ? tcoNumber.includes(tcoNumberFilter) : true;
            };

            if (tcoNumberFilter) {
                $.fn.dataTable.ext.search.push(tcoNumberFilterFunction);
            }

            try {
                dataTableRef.current
                    .column(0)
                    .search(tariffFilter, false, true)
                    .column(1)
                    .search('', false, false)
                    .column(2)
                    .search(descriptionFilter, false, true)
                    .draw();
            } catch (error) {
                isDrawing.current = false;
            }
        }, 100);

        return () => clearTimeout(timeoutId);
    }, [tariffFilter, tcoNumberFilter, descriptionFilter, isTableInitialized]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdown = document.querySelector('.pro-dropdown');
            if (dropdown && !dropdown.contains(event.target)) {
                setIsProDropdownOpen(false);
            }
        };

        if (isProDropdownOpen) {
            document.addEventListener('click', handleClickOutside, { capture: true });
        }

        return () => {
            document.removeEventListener('click', handleClickOutside, { capture: true });
        };
    }, [isProDropdownOpen]);

    const handleTariffFilterChange = useCallback((e) => {
        setTariffFilter(e.target.value);
    }, []);

    const handleTcoNumberFilterChange = useCallback((e) => {
        setTcoNumberFilter(e.target.value);
    }, []);

    const handleDescriptionFilterChange = useCallback((e) => {
        setDescriptionFilter(e.target.value);
    }, []);

    const handleLogout = () => {
        signOut(auth)
            .then(() => {
                navigate('/');
            })
            .catch((err) => console.error('Logout error:', err));
    };

    const resetTable = useCallback(() => {
        if (dataTableRef.current) {
            setTariffFilter('');
            setTcoNumberFilter('');
            setDescriptionFilter('');
            dataTableRef.current
                .column(0)
                .search('')
                .column(1)
                .search('')
                .column(2)
                .search('')
                .draw();
            dataTableRef.current.page(0).draw('page');
            applyStyles();
        }
    }, []);

    return (
        <div className="tco-viewer-container">
            <header className="header">
                <div className="header-content">
                    <img src={logo} alt="Customs Pro AI Logo" />
                    <h1>Customs Tariff Concessions</h1>
                    <nav className="nav">
                        <Link to="/" className="nav-item">Home</Link>
                        <Link to="/app" className="nav-item">Classify Goods</Link>
                        <Link to="/customs-chat" className="nav-item">Customs Chat</Link>
                        <div
                            className={`nav-item pro-dropdown ${isProDropdownOpen ? 'open' : ''}`}
                            onClick={() => setIsProDropdownOpen(!isProDropdownOpen)}
                            role="button"
                            aria-expanded={isProDropdownOpen}
                            aria-controls="pro-dropdown-menu"
                        >
                            <span className="active">Pro</span>
                            <div className="dropdown-content" id="pro-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                                <Link to="/tariff-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Tariff Viewer</Link>
                                <Link to="/tco-viewer" className="dropdown-item active" onClick={() => setIsProDropdownOpen(false)}>TCO Viewer</Link>
                                <Link to="/precedent-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Precedent Viewer</Link>
                                <Link to="/ahecc-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>AHECC Viewer</Link>
                                <Link to="/schedule4-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Schedule 4 Viewer</Link>
                                <Link to="/refund-reason-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Refund Reason Codes</Link>
                                <Link to="/gst-exemptions-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>GST Exemption Codes</Link>
                                <Link to="/customs-depots-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Customs Depot Codes</Link>
                                <Link to="/quarantine-depots-viewer" className="dropdown-item"onClick={() => setIsProDropdownOpen(false)}>Quarantine Depot Codes</Link>
                                <Link to="/history" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>History</Link>
                            </div>
                        </div>
                        <Link to="/settings" className="nav-item">Settings</Link>
                        <span className="nav-item logout" onClick={handleLogout}>Logout</span>
                    </nav>
                </div>    
            </header>
            <div className="main-content">
                <div className="table-panel">
                    <div className="table-header">
                        <div id="customFilter">
                            <input
                                type="text"
                                placeholder="Filter Tariff Classification..."
                                value={tariffFilter}
                                onChange={handleTariffFilterChange}
                                className="custom-filter-input"
                            />
                            <input
                                type="text"
                                placeholder="Filter TCO Number..."
                                value={tcoNumberFilter}
                                onChange={handleTcoNumberFilterChange}
                                className="custom-filter-input"
                            />
                            <input
                                type="text"
                                placeholder="Filter TCO Description..."
                                value={descriptionFilter}
                                onChange={handleDescriptionFilterChange}
                                className="custom-filter-input"
                            />
                        </div>
                        <button id="resetTableBtn" onClick={resetTable}>Reset Table</button>
                    </div>
                    <table id="tcoTable" className="display" ref={tableRef}></table>
                </div>
            </div>
        </div>
    );
};

export default TCOViewer;